import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Grid, Box, Button, Typography, Paper } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { LightPurpleButton } from '../components/buttonStyles';

import IITP_LOGO from '../assets/iitp_logo.png';

const Homepage = () => {
    return (
        <StyledContainer>
            <HeroSection>
                <motion.div 
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    style={{ textAlign: 'center', marginBottom: '40px' }}
                >
                    <LogoImage src={IITP_LOGO} alt="IIT Patna Logo" />
                    <StyledTitle>
                        Indian Institute of Technology Patna
                    </StyledTitle>
                    <SanskritMotto>
                        विद्यार्थी लभते विद्याम् | "One who aspires wisdom, attains it."
                    </SanskritMotto>
                </motion.div>

                <Grid container spacing={4} alignItems="center" justifyContent="center">
                    <Grid item xs={12} md={6}>
                        <motion.div 
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <InfoCard elevation={4}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0D47A1', mb: 2 }}>
                                    Enterprise Resource Planning
                                </Typography>
                                <StyledText>
                                    Welcome to the official ERP portal for IIT Patna. 
                                    Streamline institute management, manage branch organization, fees, and academic records.
                                    Seamlessly track attendance, assess performance, and provide real-time feedback.
                                </StyledText>
                                
                                <StatsBox>
                                    <StatItem>
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9933' }}>2.8k+</Typography>
                                        <Typography variant="body2" sx={{ color: '#555' }}>Total Students</Typography>
                                    </StatItem>
                                    <StatItem>
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9933' }}>160+</Typography>
                                        <Typography variant="body2" sx={{ color: '#555' }}>Faculty Members</Typography>
                                    </StatItem>
                                    <StatItem>
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9933' }}>500</Typography>
                                        <Typography variant="body2" sx={{ color: '#555' }}>Acres Campus</Typography>
                                    </StatItem>
                                </StatsBox>
                            </InfoCard>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <LoginCard elevation={6}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
                                    Access Your Portal
                                </Typography>
                                <StyledBox>
                                    <StyledLink to="/choose">
                                        <Button variant="contained" fullWidth sx={{ backgroundColor: '#0D47A1', '&:hover': { backgroundColor: '#082e6b' }, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}>
                                            Login as User
                                        </Button>
                                    </StyledLink>
                                    <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
                                        Don't have an account?{' '}
                                        <Link to="/Adminregister" style={{ color: "#0D47A1", fontWeight: 'bold' }}>
                                            Sign up as Administrator
                                        </Link>
                                    </Typography>
                                </StyledBox>
                            </LoginCard>
                        </motion.div>
                    </Grid>
                </Grid>
            </HeroSection>
        </StyledContainer>
    );
};

export default Homepage;

const StyledContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  font-family: 'Inter', sans-serif;
`;

const HeroSection = styled(Container)`
  max-width: 1200px !important;
`;

const LogoImage = styled.img`
  width: 120px;
  margin-bottom: 20px;
  filter: drop-shadow(0px 4px 6px rgba(0,0,0,0.1));
`;

const StyledTitle = styled.h1`
  font-size: 2.8rem;
  color: #0D47A1;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.5px;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.05);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SanskritMotto = styled.h3`
  font-size: 1.2rem;
  color: #FF9933;
  font-weight: 600;
  margin-top: 10px;
  font-style: italic;
`;

const InfoCard = styled(Paper)`
  padding: 40px;
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.5);
`;

const LoginCard = styled(Paper)`
  padding: 40px;
  border-radius: 20px !important;
  background: white !important;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1) !important;
`;

const StyledText = styled.p`
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.7;
  margin-bottom: 30px;
`;

const StatsBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  width: 100%;
`;
